<script setup>
import { Head, Link } from '@inertiajs/vue3';
import { ref } from 'vue';
import FrontLayout from '@/Layouts/FrontLayout.vue';
import PageHero from '@/Components/Front/PageHero.vue';

const props = defineProps({
    setting: Object,
    galleries: Object,
    title: String,
    meta_description: String,
});

const selectedImage = ref(null);

const openLightbox = (image) => {
    selectedImage.value = image;
    document.body.style.overflow = 'hidden';
};

const closeLightbox = () => {
    selectedImage.value = null;
    document.body.style.overflow = '';
};
</script>

<template>
    <FrontLayout 
        :setting="setting" 
        :title="title" 
        :metaDescription="meta_description"
    >
        <Head>

            <meta name="description" :content="meta_description" />
        </Head>

        <PageHero 
            title="Gallery Foto"
            subtitle="Dokumentasi layanan dan momen perjalanan bersama kami"
            :breadcrumbs="[
                { label: 'Beranda', url: '/' },
                { label: 'Gallery' }
            ]"
        />

        <section class="py-12 md:py-16 bg-gray-50 dark:bg-gray-900 min-h-[500px]">
            <div class="container mx-auto px-4 lg:px-6 xl:px-8 max-w-7xl">
                <header class="max-w-3xl mx-auto text-center mb-12">
                    <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-4">
                        Gallery Sewa Mobil & Rental Mobil Banyuwangi
                    </h2>
                    <p class="text-gray-600 dark:text-gray-400 leading-relaxed">
                        Dokumentasi layanan sewa mobil Banyuwangi dan rental mobil Banyuwangi dari Gotransport. Lihat koleksi armada, perjalanan wisata, dan momen pelanggan kami.
                    </p>
                </header>

                <div v-if="galleries.data && galleries.data.length > 0">
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <article 
                            v-for="gallery in galleries.data" 
                            :key="gallery.id" 
                            class="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
                            @click="openLightbox(gallery)"
                        >
                            <div class="relative overflow-hidden aspect-[4/3]">
                                <img 
                                    :src="gallery.image_url" 
                                    :alt="gallery.image_alt" 
                                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                    loading="lazy"
                                />
                                <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <i class="fa fa-search-plus text-white text-3xl drop-shadow-md transform scale-75 group-hover:scale-100 transition-transform duration-300"></i>
                                </div>
                            </div>
                            <div class="p-4">
                                <h3 class="font-bold text-gray-900 dark:text-white mb-1 line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">{{ gallery.title }}</h3>
                                <p v-if="gallery.description" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">{{ gallery.description }}</p>
                            </div>
                        </article>
                    </div>

                    <!-- Pagination -->
                    <div class="mt-12 flex justify-center" v-if="galleries.links && galleries.links.length > 3">
                        <div class="flex flex-wrap gap-2 justify-center">
                            <template v-for="(link, key) in galleries.links" :key="key">
                                <div v-if="link.url === null" class="px-3 py-2 text-sm text-gray-400 dark:text-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800" v-html="link.label" />
                                <Link v-else class="px-3 py-2 text-sm border rounded-lg transition-all duration-200" 
                                      :class="link.active ? 'bg-blue-600 text-white border-blue-600 shadow-md' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-600 dark:hover:text-blue-400 hover:border-blue-300 dark:hover:border-blue-500'"
                                      :href="link.url" v-html="link.label" />
                            </template>
                        </div>
                    </div>
                </div>

                <div v-else class="text-center py-20 bg-white dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                     <div class="w-20 h-20 bg-gray-50 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                         <i class="fa fa-camera text-gray-300 dark:text-gray-600 text-3xl"></i>
                     </div>
                     <h3 class="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">Belum Ada Foto</h3>
                     <p class="text-gray-500 dark:text-gray-400 mb-6">Gallery foto akan segera diupdate</p>
                     <Link href="/" class="inline-flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-full font-bold transition-colors">
                         <i class="fa fa-home"></i>
                         <span>Kembali ke Beranda</span>
                     </Link>
                </div>
            </div>
        </section>

        <!-- Lightbox Modal -->
        <div v-if="selectedImage" class="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 animate-fade-in" @click.self="closeLightbox">
             <button @click="closeLightbox" class="absolute top-4 right-4 text-white/70 hover:text-white transition-colors">
                 <i class="fa fa-times text-3xl"></i>
             </button>
             
             <div class="relative max-w-5xl max-h-[90vh] flex flex-col items-center">
                 <img :src="selectedImage.image_url" :alt="selectedImage.image_alt" class="max-w-full max-h-[80vh] object-contain rounded shadow-2xl" />
                 <div class="mt-4 text-center text-white">
                     <h3 class="text-xl font-bold">{{ selectedImage.title }}</h3>
                     <p class="text-white/80 mt-1 max-w-2xl">{{ selectedImage.description }}</p>
                 </div>
             </div>
        </div>
    </FrontLayout>
</template>

<style scoped>
.animate-fade-in {
    animation: fadeIn 0.2s ease-out;
}
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
</style>
